import { type Contact, contactsService } from "@/app/providers/services";
import { type ContactIdentity, ContactIdentityStatus } from "./ContactIdentityStatus";
import styled from "@emotion/styled";
import { debounce } from "lodash";
import moment from "moment";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Roles, useHasRole } from "@/auth/permissions";
import { useNotify } from "@/common/notifications";
import { Tooltip } from "@/components";
import { type UserLike, getUserDisplayName } from "@/lib/user";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import { getValueAtDate } from "@imminently/decisively-core";
import Button from "@imminently/decisively-ui/Button";
import { useConfirmDialog } from "@imminently/decisively-ui/ConfirmDialog";
import DataTable, {
  type DataTableColumn,
  type DataTableRowParams,
  type DataTableSortModel,
} from "@imminently/decisively-ui/DataTable";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Icon from "@imminently/decisively-ui/Icon";
import TextInput from "@imminently/decisively-ui/TextInput";
import { useTranslation } from "react-i18next";

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  flex: 1;

  .MuiDataGrid-root {
    border: none;
  }

  .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within,
  .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within {
    outline: none;
  }
`;

const formatDate = (date: string) => {
  // use moment to format date as DD MMM, YYYY
  return moment(date).format("DD MMM, YYYY");
};

const DEFAULT_SORT_MODEL: DataTableSortModel = [{ field: "lastModified", sort: "desc" }];

const getBackendSortField = (field?: string) => {
  if (field === "id") return "firstName";
  return field || "lastModified";
};

const NameCell = (contact: Contact) => {
  // these may not exist
  const firstName = getValueAtDate(contact.firstName, new Date()) as string;
  const lastName = getValueAtDate(contact.lastName, new Date()) as string;
  const fullName = getUserDisplayName({ firstName, lastName } as UserLike).trim();
  const hasName = fullName.length > 0;
  return (
    <div className="flex flex-col">
      <span className="text-base">{hasName ? fullName : contact.id}</span>
      {hasName ? <span className="text-xs text-muted-foreground">{contact.id}</span> : null}
    </div>
  );
};

export const DateCell = (field: keyof Contact, label: string, date: string | undefined) => {
  // moment(undefined) is "now", so a contact without the date would read "a few seconds ago".
  const value = date ? moment(date) : undefined;
  if (!value?.isValid()) return <span className="text-muted-foreground">—</span>;
  return (
    <Tooltip title={`${label}: ${date}`}>
      <span>{value.fromNow()}</span>
    </Tooltip>
  );
};

export type ContactTableProps = {
  onClick: (contact: any) => void;
  onDeleted?: (contact: Contact) => void;
  searchInput: string;
  searchTerm: string;
  onSearchInputChange: (value: string) => void;
  onSearchTermChange: (value: string) => void;
  /** The contact open in the panel, shown as the selected row. */
  selectedContactId?: string | null;
  /** Login statuses checked since the list loaded (by opening contacts), by contact ID. */
  checkedIdentities?: Record<string, ContactIdentity>;
};

export const ContactTable = ({
  onClick,
  onDeleted,
  searchInput,
  searchTerm,
  onSearchInputChange,
  onSearchTermChange,
  checkedIdentities,
  selectedContactId,
}: ContactTableProps) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const { pushConfirmDialog } = useConfirmDialog();
  const canDeleteContacts = useHasRole([Roles.ContactsWriter, Roles.System]);
  const [sortModel, setSortModel] = useState<DataTableSortModel>(DEFAULT_SORT_MODEL);

  // Debounce the search term update
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => onSearchTermChange(value), 500),
    [onSearchTermChange],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  const fullFilter = useMemo(() => {
    const [sort] = sortModel;
    const base = {
      sort: {
        field: getBackendSortField(sort?.field),
        order: sort?.sort === "asc" ? "asc" : "desc",
      } as const,
    };
    if (searchTerm.trim()) {
      return { ...base, searchName: searchTerm.trim() };
    }
    return base;
  }, [searchTerm, sortModel]);

  const {
    data: contacts,
    total,
    query,
    pagination,
  } = contactsService.useResources({
    filter: fullFilter,
    refetchOnWindowFocus: false,
  });
  const deleteMutation = contactsService.useDelete();
  const rows = useMemo(
    () => (contacts || []).map((contact) => {
      const checked = checkedIdentities?.[String(contact.id)];
      return checked ? { ...contact, ...checked } : contact;
    }),
    [contacts, checkedIdentities],
  );

  // Reset pagination when search term changes (debounced)
  useEffect(() => {
    pagination.setPage(0);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      onSearchInputChange(value);
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm, onSearchInputChange],
  );

  const deleteContact = useCallback(
    async (contact: Contact) => {
      try {
        await (deleteMutation.mutateAsync as any)({
          id: contact.id,
          payload: {},
        });
        notify.success("Contact deleted.");
        onDeleted?.(contact);
        query.refetch();
      } catch (error: any) {
        notify.error(error?.message || "Failed to delete contact.");
      }
    },
    [deleteMutation.mutateAsync, notify, onDeleted, query],
  );

  const confirmDeleteContact = useCallback(
    (contact: Contact) => {
      pushConfirmDialog({
        layer: WORKSPACE_LAYER,
        title: "Delete contact",
        description: `Delete contact ${contact.id}? This cannot be undone.`,
        confirmLabel: "Delete",
        confirmIntent: "destructive",
        onConfirm: () => deleteContact(contact),
      });
    },
    [deleteContact, pushConfirmDialog],
  );

  const columns: DataTableColumn[] = [
    {
      field: "id",
      headerName: "Contact",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => NameCell(params.row as Contact),
    },
    {
      field: "identityId",
      headerName: "User",
      width: 120,
      sortable: false,
      renderCell: (params) => <ContactIdentityStatus contact={params.row as Contact} />,
    },
    {
      field: "created",
      headerName: "Created",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => DateCell("created", "Created", params.row.created),
    },
    {
      field: "lastModified",
      headerName: "Last updated",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => DateCell("lastModified", "Last updated", params.row.lastModified),
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const contact = params.row as Contact;
        return (
          <DropdownMenu>
            <DropdownMenu.Trigger asChild>
              <Button
                icon
                size="sm"
                variant="ghost"
                label="Contact actions"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <Icon name="show-more" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              <DropdownMenu.Item
                color="destructive"
                disabled={!canDeleteContacts || deleteMutation.isLoading}
                onClick={() => confirmDeleteContact(contact)}
              >
                <DropdownMenu.Item.Label>Delete</DropdownMenu.Item.Label>
                <DropdownMenu.Item.Icon>
                  <Icon name="delete" />
                </DropdownMenu.Item.Icon>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <TableContainer>
      <div className="flex items-center justify-end gap-4 px-4 py-2 border-b">
        <TextInput
          startAdornment={<Icon name="search" />}
          placeholder={t("contacts.search")}
          value={searchInput}
          onChange={handleSearchChange}
          className="w-100"
        />
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        selectedRowId={selectedContactId}
        rowCount={total}
        loading={query.isLoading || query.isFetching}
        onRowClick={(params: DataTableRowParams) => onClick(params.row)}
        pagination
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model.length > 0 ? model : DEFAULT_SORT_MODEL);
          pagination.setPage(0);
        }}
        paginationModel={{
          page: pagination.pagination.page,
          pageSize: pagination.pagination.perPage,
        }}
        onPaginationModelChange={(model) => {
          if (model.page !== pagination.pagination.page) {
            pagination.setPage(model.page);
          }
          if (model.pageSize !== pagination.pagination.perPage) {
            pagination.setPerPage(model.pageSize);
          }
        }}
        getRowId={(row) => row.id}
      />
    </TableContainer>
  );
};
